import { useEffect, useRef, useState } from 'react';
import * as pdfjs from 'pdfjs-dist';

/**
 * PDF.js viewer — renders ONE page of a PDF at a time onto a canvas.
 *
 * Why one-page-at-a-time and not the full pdfjs-dist viewer:
 *   pdfjs-dist ships a full viewer app (web/viewer.html, ~1MB) but
 *   it expects to live in its own page with its own DOM scaffolding.
 *   Embedding it inside an arbitrary React layout is painful. Rendering
 *   pages directly to canvas is the documented "library mode" approach.
 *
 * Worker setup: PDF.js does parsing on a Web Worker so the UI stays
 * responsive. Vite needs the worker URL resolved relative to the
 * bundled assets — we use `?url` import to get a stable URL. This
 * matches the pdfjs-dist docs for Vite/webpack5 setups.
 *
 * State machine:
 *   props.url changes               → load doc, render page
 *   props.page changes              → render that page
 *   zoom slider                     → re-render current page
 *   any of above + previous render  → cancel in-flight render
 *
 * Errors handled:
 *   - presigned URL expired (signal: 403 from S3)        → "Refresh the page"
 *   - PDF binary corrupted                                → "Could not parse"
 *   - page out of range                                   → "Page N of M (clamped)"
 *   - browser blocked the worker (rare)                   → "PDF rendering unavailable"
 *
 * Performance: re-render on every prop change. PDF.js caches pages
 * internally so a re-render of an already-loaded page is cheap (~50ms).
 * Switching pages is ~200ms on a typical DP statement.
 */

// PDF.js worker URL — bundled by Vite, resolved at build time.
// The ?url suffix tells Vite to emit the file and give us a string path.
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

export function PdfViewer({
  url,
  page,
  onPageChange,
  onTotalPages,
}: {
  url: string | null;
  page: number;
  onPageChange?: (page: number) => void;
  onTotalPages?: (total: number) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const docRef = useRef<pdfjs.PDFDocumentProxy | null>(null);
  const renderTaskRef = useRef<pdfjs.RenderTask | null>(null);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [zoom, setZoom] = useState<number>(1.25);

  // Load the document whenever the URL changes. URLs are presigned
  // S3 links that expire in 5 minutes — if a reviewer keeps the page
  // open longer than that, the next page-jump will need a fresh URL.
  useEffect(() => {
    if (!url) {
      docRef.current = null;
      setTotalPages(0);
      return;
    }
    setLoading(true);
    setError(null);
    const task = pdfjs.getDocument({ url });
    let cancelled = false;
    task.promise
      .then(doc => {
        if (cancelled) { doc.destroy(); return; }
        docRef.current = doc;
        setTotalPages(doc.numPages);
        onTotalPages?.(doc.numPages);
        setLoading(false);
      })
      .catch(err => {
        if (cancelled) return;
        setLoading(false);
        // Most likely cause: presigned URL expired (S3 returns 403) or
        // CORS misconfig. Both manifest as a network-ish error here.
        const msg = err?.message ?? String(err);
        setError(
          msg.includes('403') || msg.toLowerCase().includes('expired')
            ? 'Download link expired. Refresh the page to get a new one.'
            : `Could not load PDF: ${msg}`
        );
      });
    return () => {
      cancelled = true;
      // Don't destroy on URL change — the new doc will replace docRef.
      // Cancelling renderTask handles in-flight render.
      renderTaskRef.current?.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  // Render the requested page whenever page, zoom, or doc changes.
  useEffect(() => {
    const doc = docRef.current;
    const canvas = canvasRef.current;
    if (!doc || !canvas) return;
    // Clamp to valid range — caller might request page 41 from a 6-page PDF.
    const targetPage = Math.max(1, Math.min(doc.numPages, Math.floor(page)));

    let cancelled = false;
    renderTaskRef.current?.cancel();
    setError(null);

    doc.getPage(targetPage)
      .then(pageObj => {
        if (cancelled) return;
        const viewport = pageObj.getViewport({ scale: zoom });
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          setError('Canvas 2D context unavailable.');
          return;
        }
        // Device-pixel-ratio for crisp text on retina displays.
        const dpr = window.devicePixelRatio || 1;
        canvas.width  = Math.floor(viewport.width  * dpr);
        canvas.height = Math.floor(viewport.height * dpr);
        canvas.style.width  = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

        const task = pageObj.render({ canvasContext: ctx, viewport });
        renderTaskRef.current = task;
        task.promise.catch((err: unknown) => {
          // RenderTask.cancel() rejects with a 'RenderingCancelledException' —
          // expected and not an error.
          const name = (err as { name?: string } | null)?.name;
          if (name === 'RenderingCancelledException') return;
          setError(`Render failed: ${(err as Error)?.message ?? String(err)}`);
        });
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setError(`Could not load page ${targetPage}: ${err.message}`);
      });

    return () => { cancelled = true; };
  }, [page, zoom, totalPages]);

  // Notify parent if the page got clamped — caller's local "current page"
  // state will be wrong otherwise.
  useEffect(() => {
    if (!totalPages) return;
    const clamped = Math.max(1, Math.min(totalPages, Math.floor(page)));
    if (clamped !== page) onPageChange?.(clamped);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, totalPages]);

  return (
    <div className="flex flex-col h-full">
      <div className="px-3 py-2 border-b border-axis-cloud-200 flex items-center gap-2"
           style={{ background: 'var(--bg-elevated)' }}>
        <button
          type="button"
          className="text-xs px-2 py-1 rounded border border-axis-cloud-200 disabled:opacity-50"
          onClick={() => onPageChange?.(Math.max(1, page - 1))}
          disabled={page <= 1 || loading || totalPages === 0}
        >‹ Prev</button>
        <span className="text-xs" style={{ color: 'var(--text-strong)' }}>
          Page {totalPages === 0 ? '—' : `${page} / ${totalPages}`}
        </span>
        <button
          type="button"
          className="text-xs px-2 py-1 rounded border border-axis-cloud-200 disabled:opacity-50"
          onClick={() => onPageChange?.(Math.min(totalPages, page + 1))}
          disabled={page >= totalPages || loading || totalPages === 0}
        >Next ›</button>
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            className="text-xs px-2 py-1 rounded border border-axis-cloud-200 disabled:opacity-50"
            onClick={() => setZoom(z => Math.max(0.5, z - 0.25))}
            disabled={zoom <= 0.5}
            aria-label="Zoom out"
          >−</button>
          <span className="text-[11px] tabular-nums" style={{ color: 'var(--text-200)' }}>
            {Math.round(zoom * 100)}%
          </span>
          <button
            type="button"
            className="text-xs px-2 py-1 rounded border border-axis-cloud-200 disabled:opacity-50"
            onClick={() => setZoom(z => Math.min(3, z + 0.25))}
            disabled={zoom >= 3}
            aria-label="Zoom in"
          >+</button>
        </div>
      </div>

      <div
        className="flex-1 overflow-auto p-3 flex justify-center items-start"
        style={{ background: 'var(--bg-surface)' }}
      >
        {!url && (
          <div className="text-sm py-12" style={{ color: 'var(--text-200)' }}>
            Select an ISIN on the left to view its source page.
          </div>
        )}
        {loading && (
          <div className="text-sm py-12" style={{ color: 'var(--text-200)' }}>Loading PDF…</div>
        )}
        {error && (
          <div className="text-sm py-12 max-w-md text-center"
               style={{ color: 'var(--status-warn)' }}>
            {error}
          </div>
        )}
        {!loading && !error && url && (
          <canvas
            ref={canvasRef}
            className="border border-axis-cloud-200 shadow-sm"
            style={{ background: 'white' }}
          />
        )}
      </div>
    </div>
  );
}
